import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { TableCell, TableRow } from '@material-ui/core';
import { observer } from 'mobx-react';
import { invoiceChannelLabel } from '@ourbranch/document-utils';
import { isPlaceholderEmail } from '@ourbranch/policy-utils';

import { Loading, Paper, Table } from 'core';
import Section from 'core/components/section';
import { Tag } from 'core/components/tag';
import { isCondensedLayout } from 'core/components/theme/condensed-layout';
import { Button } from 'core/components/button';
import { useStore } from 'core/store';
import { apolloClient } from 'core/apollo-client';
import { openDocument } from 'core/helpers/document-download';
import { dateTimeFormatter } from 'core/helpers/formatters';
import { GET_INVOICE_HISTORY } from './invoice-history.queries';
import { ResendInvoiceDialog } from './resend-invoice-dialog';
import useStyles from './invoice-history.styles';

const NOT_RECORDED = '—';

const installmentLabel = ({ installmentNumber, totalInstallments }) =>
  installmentNumber ? `${installmentNumber} of ${totalInstallments || installmentNumber}` : NOT_RECORDED;

/**
 * Invoice history on the Customer page (#36679, mutual brands, internal staff): every invoice PDF
 * saved for the account, newest first, with how it went out (scheduled, email, letter, generated), to
 * whom, the amount due, due date and installment. View / Print opens the exact copy that was saved;
 * Resend (staff who can edit policies) sends a fresh invoice for the current amount due. Invoices from before the history existed
 * show what could be recovered.
 */
const InvoiceHistory = observer(function InvoiceHistory({ canResend }) {
  const classes = useStyles();
  const {
    account: { id: accountId, email }
  } = useStore();

  const [invoices, setInvoices] = useState(null);
  const [failed, setFailed] = useState(false);
  const [resendPolicyId, setResendPolicyId] = useState(null);

  const loadInvoices = useCallback(async () => {
    setFailed(false);
    try {
      const { data } = await apolloClient.query({
        query: GET_INVOICE_HISTORY,
        variables: { accountId },
        fetchPolicy: 'no-cache'
      });
      setInvoices(data?.documents?.invoices || []);
    } catch {
      setFailed(true);
    }
  }, [accountId]);

  useEffect(() => {
    if (accountId) {
      loadInvoices();
    }
  }, [accountId, loadInvoices]);

  let content;
  if (failed) {
    content = <Paper>The invoice history could not be loaded.</Paper>;
  } else if (!invoices) {
    content = <Loading />;
  } else if (!invoices.length) {
    content = <Paper>No invoices have been sent for this member.</Paper>;
  } else {
    content = (
      <Table
        selfContained={false}
        header={
          <>
            <TableCell>DATE</TableCell>
            <TableCell>POLICY ID</TableCell>
            <TableCell>SENT BY</TableCell>
            <TableCell>RECIPIENT</TableCell>
            <TableCell>AMOUNT DUE</TableCell>
            <TableCell>DUE DATE</TableCell>
            <TableCell>INSTALLMENT</TableCell>
            <TableCell>INVOICE #</TableCell>
            <TableCell />
          </>
        }
        body={invoices.map((invoice) => (
          <TableRow key={invoice.path} data-testid={`invoice-row-${invoice.path}`}>
            <TableCell>{dateTimeFormatter(new Date(invoice.lastModified))}</TableCell>
            <TableCell>{invoice.policyId}</TableCell>
            <TableCell>{invoiceChannelLabel(invoice.channel) || NOT_RECORDED}</TableCell>
            <TableCell className={classes.recipient}>{invoice.recipient || NOT_RECORDED}</TableCell>
            <TableCell>{invoice.amountDue || NOT_RECORDED}</TableCell>
            <TableCell>{invoice.dueDate || NOT_RECORDED}</TableCell>
            <TableCell>{installmentLabel(invoice)}</TableCell>
            <TableCell>{invoice.invoiceNumber || NOT_RECORDED}</TableCell>
            <TableCell className={classes.actions}>
              <Button
                variant="text"
                color="secondary"
                onClick={() => openDocument({ path: invoice.path, accountId, flow: 'invoiceHistory' })}
              >
                View / Print
              </Button>
              {canResend && (
                <Button variant="text" color="secondary" onClick={() => setResendPolicyId(invoice.policyId)}>
                  Resend
                </Button>
              )}
            </TableCell>
          </TableRow>
        ))}
      />
    );
  }

  return (
    <Section
      id="invoiceHistory"
      className={classes.section}
      title="Invoices"
      titleAction={isCondensedLayout() ? <Tag variant="internal">Internal</Tag> : undefined}
    >
      {content}
      {resendPolicyId && (
        <ResendInvoiceDialog
          policyId={resendPolicyId}
          hasEmail={!!email?.trim() && !isPlaceholderEmail(email)}
          hasPlaceholderEmail={isPlaceholderEmail(email)}
          onClose={() => {
            setResendPolicyId(null);
            loadInvoices();
          }}
        />
      )}
    </Section>
  );
});

InvoiceHistory.propTypes = {
  // Staff who can edit policies, the billing tab Send's gate; others can only view and print.
  canResend: PropTypes.bool
};

InvoiceHistory.defaultProps = {
  canResend: false
};

export default InvoiceHistory;
